import { buildNpsResponseCounts, buildNpsSurveyWow, parseNpsSurveyWowResults } from '@pendo/middleware';
import { runAggregationOnce } from '@/utils/aggregation';
import { isNpsGuide } from '@/vuex/modules/guides/guides.utils.js';
import { i18n_t as $t } from '@/utils/i18n';
import get from 'lodash/get';
import moment from '@utils/moment-wrapper';
import momentTimeZone from 'moment-timezone';

export const LOW_N_THRESHOLD = 10;

export function getInitialState () {
    return {
        insight: null,
        isLoading: false,
        error: null,
    };
}

function npsRatingPollId (guide) {
    const ratingPoll = (guide?.polls || []).find((poll) => poll?.attributes?.type === 'NPSRating');

    return ratingPoll?.id || null;
}

export function resolveNpsSurvey (guides = [], responseCounts = {}, appId = undefined) {
    let npsGuides = guides.filter((guide) => isNpsGuide(guide) && npsRatingPollId(guide));

    const numericAppId = Number(appId);
    if (!isNaN(numericAppId) && numericAppId > 0) {
        npsGuides = npsGuides.filter((guide) => guide.appId === numericAppId);
    }

    if (npsGuides.length === 0) {
        return null;
    }

    const chosen =
        npsGuides.length === 1
            ? npsGuides[0]
            : npsGuides.reduce((best, guide) => {
                return (responseCounts[guide.id] || 0) > (responseCounts[best.id] || 0) ? guide : best;
            });

    return {
        appId: chosen.appId,
        guideId: chosen.id,
        pollId: npsRatingPollId(chosen),
        surveyName: chosen.name,
    };
}

export function buildMwSettings ({ appId, guideId, pollId, segmentId = undefined, includeAnonymous = false }) {
    return {
        appId,
        guideId,
        pollId,
        segmentId,
        hasShowAnonymousFlag: !!includeAnonymous,
    };
}

function wrapAsAggConnect (pipelineSpec) {
    return {
        name: pipelineSpec.name,
        productArea: pipelineSpec.productArea,
        subProductArea: pipelineSpec.subProductArea,
        responseFormat: 'rows',
        build: () => pipelineSpec.pipeline,
    };
}

export function extractRows (aggResult) {
    if (Array.isArray(aggResult)) return aggResult;
    if (aggResult && Array.isArray(aggResult.rows)) return aggResult.rows;
    if (aggResult && Array.isArray(aggResult.messages)) {
        const rows = [];
        for (const message of aggResult.messages) {
            if (message && Array.isArray(message.rows)) {
                rows.push(...message.rows);
            }
        }

        return rows;
    }

    return [];
}

export async function fetchNpsResponseCounts () {
    const spec = buildNpsResponseCounts();
    const rows = await runAggregationOnce(wrapAsAggConnect(spec)).then(extractRows);
    const counts = {};
    for (const row of rows) {
        if (row.guideId) {
            counts[row.guideId] = row.responseCount || 0;
        }
    }

    return counts;
}

export async function executeNpsPipeline (settings) {
    const { current, previous } = buildNpsSurveyWow(settings);

    const [currentRows, previousRows] = await Promise.all([
        runAggregationOnce(wrapAsAggConnect(current)).then(extractRows),
        runAggregationOnce(wrapAsAggConnect(previous)).then(extractRows),
    ]);
    const parsed = parseNpsSurveyWowResults({ currentRows, previousRows });

    return parsed;
}

function nowInSubZone (rootGetters) {
    const zone = get(rootGetters?.['subscription/getActiveSub'], 'timeZone');

    return zone ? momentTimeZone.tz(zone) : moment(undefined, { ignoreTimeZone: true });
}

export function buildPeriodLabels (rootGetters) {
    const yesterday = nowInSubZone(rootGetters).startOf('day').subtract(1, 'day');
    const currentStart = yesterday.clone().subtract(6, 'days');
    const previousEnd = currentStart.clone().subtract(1, 'day');
    const previousStart = previousEnd.clone().subtract(6, 'days');

    const range = (start, end) => `${start.format('MMMM D')}–${end.format('MMMM D')}`;

    return {
        currentPeriodLabel: range(currentStart, yesterday),
        previousPeriodLabel: range(previousStart, previousEnd),
    };
}

export function buildAppData (rootGetters, appId) {
    const app = rootGetters['apps/getAppById']?.(appId);
    const rawIcon = rootGetters['apps/getIconByAppId']?.(appId);
    const appIcon = typeof rawIcon === 'string' ? { type: rawIcon, size: 12 } : { ...rawIcon, size: 12 };

    return {
        name: app?.displayName || app?.name,
        icon: appIcon,
        appId,
    };
}

export function buildNpsSignal (parsed, { appData, periodLabels, segmentId, surveyName }) {
    if (!parsed || parsed.currentN == null || parsed.currentN < LOW_N_THRESHOLD) {
        return null;
    }

    const { currentScore, previousScore, delta, pctChange } = parsed;
    if (delta === 0) {
        return null;
    }

    const isIncrease = delta > 0;

    const title = $t('signals.npsTitle');
    const headline = isIncrease ? $t('signals.npsHeadlineUp') : $t('signals.npsHeadlineDown');

    const textAfterArgs = {
        currentScore: Math.round(currentScore),
        previousScore: Math.round(previousScore),
        currentPeriod: periodLabels.currentPeriodLabel,
        pctChange: pctChange == null ? null : Math.abs(Math.round(pctChange)),
    };

    let textAfterTagName;
    if (pctChange == null) {
        textAfterTagName = $t('signals.npsTextAfterNoPct', textAfterArgs);
    } else if (isIncrease) {
        textAfterTagName = $t('signals.npsTextAfterIncrease', textAfterArgs);
    } else {
        textAfterTagName = $t('signals.npsTextAfterDecrease', textAfterArgs);
    }

    return {
        type: 'npsWeekOverWeek',
        kind: 'survey',
        title,
        headline,
        delta,
        pctChange: pctChange == null ? null : Math.round(pctChange),
        currentDataPoint: Math.round(currentScore),
        previousDataPoint: Math.round(previousScore),
        currentPeriodLabel: periodLabels.currentPeriodLabel,
        previousPeriodLabel: periodLabels.previousPeriodLabel,
        textBeforeTagName: $t('signals.npsTextBefore'),
        textAfterTagName,
        showAppTag: true,
        tagName: appData?.name,
        appIcon: appData?.icon,
        appId: appData?.appId,
        surveyName,
        segmentId,
    };
}
